import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CalendarCheck2, CalendarX2, Download } from 'lucide-react';
import { bookingsApi, type MissedBookingRow } from '@/features/bookings/bookings.api';
import { useT, useLang } from '@/i18n';
import { toast } from '@/stores/toast.store';
import { cn } from '@/lib/utils';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { useAgents } from '@/features/reassignment/useReassignment';

/**
 * F5-T6c · Admin 11 — «Citas perdidas» (contrato 2026-08-28).
 *
 * Pantalla PROPIA, cola de trabajo con acciones: encabezado ámbar con el
 * conteo y la regla del no-show, pills de tramo de antigüedad (filtros, NO
 * secciones), lista de más vieja a más nueva con cápsula de antigüedad,
 * paciente (ID Medicaid enmascarado), quién agendó y DOS acciones por fila.
 *
 * MUERE por contrato (dies): los tramos week/month/all viejos (los
 * reemplazan los tramos dibujados) · «Assign to me», el select de reasignar
 * y «Done»/marcar completa («dos acciones por fila, no cinco»; el cierre
 * vive en el caso) · window.confirm (murió con el select) · el rojo.
 *
 * La acción principal la calcula el SISTEMA: 'queue' si el agente que agendó
 * ya no está activo (medible con la lista de agentes), si no 'resched'.
 *
 * NECESITA-SERVER:
 * - Intentos «N de 5» por paciente: MissedBookingRow no trae el conteo de
 *   intentos → la columna Intentos y el modo 'unlock' («Desbloquear y
 *   reagendar», 5 de 5) no se dibujan; la tarjeta teal de agotados tampoco.
 * - Nombre completo del paciente: el endpoint trae solo apellido
 *   (patientLastName) — el contrato pide apellidos completos, se muestra lo
 *   que el server da.
 * - Export CSV auditado: el export con nombres tiene que salir AUDITADO del
 *   server; no existe el endpoint → botón deshabilitado con diagnóstico
 *   (armar un CSV client-side sería un export de PHI sin auditar).
 * - «Devolver a la cola»: no existe camino de server para devolver una cita
 *   a la cola general → botón deshabilitado con diagnóstico.
 */

const PAGE_SIZE = 25;

type Bucket = 'all' | 'today' | 'b13' | 'b47' | 'b7p';

interface AgedRow {
  row: MissedBookingRow;
  hours: number;
  days: number;
  mode: 'resched' | 'queue';
}

function bucketOf(days: number): Exclude<Bucket, 'all'> {
  if (days <= 0) return 'today';
  if (days <= 3) return 'b13';
  if (days <= 7) return 'b47';
  return 'b7p';
}

export default function MissedBookingsPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();
  const locale = lang === 'es' ? 'es' : 'en';

  const [bucket, setBucket] = useState<Bucket>('all');
  const [page, setPage] = useState(0);
  const [reschedId, setReschedId] = useState<string | null>(null);
  const [newDate, setNewDate] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [liveMsg, setLiveMsg] = useState('');

  const { data: missedRaw, isLoading, error, refetch } = useQuery({
    queryKey: ['bookings', 'missed'],
    queryFn: () => bookingsApi.missed(100, 0),
  });

  // El modo 'queue' se calcula con la lista de agentes (activo/inactivo).
  const agentsQ = useAgents();

  const all: AgedRow[] = useMemo(() => {
    const list: MissedBookingRow[] = Array.isArray(missedRaw)
      ? missedRaw
      : (missedRaw?.data ?? []);
    const agents = agentsQ.data ?? [];
    const now = Date.now();
    return list
      .map((row) => {
        const hours = Math.max(0, Math.floor((now - new Date(row.scheduledAt).getTime()) / 3_600_000));
        const days = Math.floor(hours / 24);
        const agent = agents.find((a) => a.id === row.agentId);
        // Agente desconocido → 'resched' (no se declara inactivo sin medirlo).
        const mode: AgedRow['mode'] = agent && !agent.active ? 'queue' : 'resched';
        return { row, hours, days, mode };
      })
      // De más vieja a más nueva: lo que decide el orden es la demora.
      .sort((a, b) => new Date(a.row.scheduledAt).getTime() - new Date(b.row.scheduledAt).getTime());
  }, [missedRaw, agentsQ.data]);

  const counts = useMemo(() => {
    const c: Record<Bucket, number> = { all: all.length, today: 0, b13: 0, b47: 0, b7p: 0 };
    for (const r of all) c[bucketOf(r.days)] += 1;
    return c;
  }, [all]);

  const filtered = useMemo(
    () => (bucket === 'all' ? all : all.filter((r) => bucketOf(r.days) === bucket)),
    [all, bucket],
  );

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const visible = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const anyQueue = visible.some((r) => r.mode === 'queue');

  const pickBucket = (b: Bucket): void => {
    setBucket(b);
    setPage(0);
  };

  const formatWhen = (iso: string): string => {
    const d = new Date(iso);
    const day = d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
    const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return `${day} ${time}`;
  };

  const doReschedule = async (row: MissedBookingRow, when: string): Promise<void> => {
    setPendingId(row.id);
    try {
      // Maquinaria conservada tal cual: update de la cita a la fecha nueva.
      await bookingsApi.update(parseInt(row.id), { scheduledAt: when, status: 'scheduled' });
      const nice = new Date(when).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
      setLiveMsg(t('f5t6c.missed.aria.resched', { date: nice }));
      setReschedId(null);
      setNewDate('');
      refetch();
    } catch {
      toast.error(t('f5t6c.missed.error.title'), '');
    } finally {
      setPendingId(null);
    }
  };

  const pills: Array<{ id: Bucket; label: string }> = [
    { id: 'all', label: t('f5t6c.missed.bucket.all') },
    { id: 'today', label: t('f5t6c.missed.bucket.today') },
    { id: 'b13', label: t('f5t6c.missed.bucket.b13') },
    { id: 'b47', label: t('f5t6c.missed.bucket.b47') },
    // Decisión 1 del contrato: el texto de tramos nombra «más de 7»; el mock
    // dibuja 4 pills. Default declarado del contrato: agregar la pill. Si el
    // CEO firma «las 4 dibujadas son ley», esta línea se borra.
    { id: 'b7p', label: t('f5t6c.missed.bucket.b7p') },
  ];

  return (
    <div className="max-w-[1440px]">
      <p aria-live="polite" role="status" className="sr-only">
        {liveMsg}
      </p>

      <header className="max-w-[760px] mb-6">
        <h1 className="font-head text-[32px] font-extrabold tracking-[-0.025em] leading-tight text-nav-navy mb-2">
          {t('f5t6c.missed.title')}
        </h1>
        <p className="font-body text-[15px] leading-relaxed text-nav-navy/90 m-0">
          {t('f5t6c.missed.subtitle')}
        </p>
      </header>

      <section className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-8 pt-7 pb-[34px] animate-fade-up-slow motion-reduce:animate-none">
        {error ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
            <ErrorState
              icon={<AlertCircle className="h-5 w-5" aria-hidden />}
              title={t('f5t6c.missed.error.title')}
              body={t('f5t6c.missed.error.body')}
              action={{ label: t('f5t6c.missed.error.retry'), onClick: () => refetch() }}
            />
          </div>
        ) : isLoading ? (
          <div className="space-y-4" aria-busy="true">
            <Skeleton className="h-[92px] w-full rounded-[12px]" />
            <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] p-4 space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-[78px] w-full" />
              ))}
            </div>
          </div>
        ) : all.length === 0 ? (
          <div className="max-w-[640px] mx-auto bg-white border-[1.5px] border-nav-card rounded-[12px] py-6">
            <EmptyState
              icon={<CalendarCheck2 className="h-6 w-6" aria-hidden />}
              title={t('f5t6c.missed.empty.title')}
              body={t('f5t6c.missed.empty.body')}
              action={{ label: t('f5t6c.missed.empty.cta'), onClick: () => navigate('/admin/reports') }}
            />
          </div>
        ) : (
          <>
            {/* TARJETA ENCABEZADO ÁMBAR — la regla del no-show, EN pantalla. */}
            <div className="flex items-start gap-4 bg-nav-amberTint border-[1.5px] border-nav-amber rounded-[12px] px-[22px] py-5 text-nav-amberText">
              <span className="w-10 h-10 flex-none rounded-full bg-white inline-flex items-center justify-center">
                <CalendarX2 className="w-5 h-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <h2 className="font-head text-[17px] font-bold m-0">
                  {all.length === 1
                    ? t('f5t6c.missed.header.countOne')
                    : t('f5t6c.missed.header.count', { n: all.length })}
                </h2>
                <p className="font-body text-[13.5px] leading-[1.55] m-0 mt-1">
                  {t('f5t6c.missed.header.sub')}
                </p>
              </div>
            </div>

            {/* BARRA DE FILTROS: tramos como botones aria-pressed + export. */}
            <div className="flex items-center gap-3 flex-wrap my-[22px]">
              <div className="flex items-center gap-2 flex-wrap">
                {pills.map((p) => {
                  const active = bucket === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => pickBucket(p.id)}
                      className={cn(
                        'inline-flex items-center gap-2 min-h-[44px] px-[15px] rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors duration-150',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                        active
                          ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
                          : 'bg-white border-nav-line text-nav-navy hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark',
                      )}
                    >
                      {p.label}
                      <span className={cn('tabular-nums', active ? 'text-white/75' : 'text-nav-faint')}>
                        {counts[p.id]}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="ml-auto">
                {/* NECESITA-SERVER: export auditado. Patrón «diagnóstico del
                    deshabilitado»: el porqué, al lado. */}
                <button
                  type="button"
                  disabled
                  aria-describedby="missed-export-why"
                  className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-card bg-[#f2f5f7] font-head text-[13px] font-bold text-nav-faint cursor-not-allowed"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  {t('f5t6c.missed.export')}
                </button>
              </div>
            </div>
            <p id="missed-export-why" className="font-body text-[12.5px] text-nav-slate -mt-3 mb-4 text-right m-0">
              {t('f5t6c.missed.exportDisabled')}
            </p>

            {filtered.length === 0 ? (
              <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
                <EmptyState
                  icon={<CalendarCheck2 className="h-5 w-5" aria-hidden />}
                  title={t('f5t6c.missed.bucketEmpty.title')}
                  body={t('f5t6c.missed.bucketEmpty.body')}
                  action={{ label: t('f5t6c.missed.bucketEmpty.cta'), onClick: () => pickBucket('all') }}
                />
              </div>
            ) : (
              <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] overflow-hidden">
                <ul>
                  {visible.map((aged, i) => (
                    <MissedRow
                      key={aged.row.id}
                      aged={aged}
                      index={i}
                      t={t}
                      formatWhen={formatWhen}
                      rescheduling={pendingId === aged.row.id}
                      pickerOpen={reschedId === aged.row.id}
                      newDate={newDate}
                      setNewDate={setNewDate}
                      onOpenPicker={() => {
                        setReschedId(aged.row.id);
                        setNewDate('');
                      }}
                      onCancelPicker={() => setReschedId(null)}
                      onConfirm={() => {
                        if (newDate) void doReschedule(aged.row, newDate);
                      }}
                    />
                  ))}
                </ul>

                {/* PIE: paginado + la regla de la propiedad de la cita. */}
                <div className="flex items-center justify-between gap-4 flex-wrap px-6 py-4 border-t border-nav-hair">
                  <p className="font-body text-[13px] text-nav-slate tabular-nums m-0">
                    {t('f5t6c.missed.pageLabel', {
                      n: visible.length,
                      total: all.length,
                      page: safePage + 1,
                      pages,
                    })}
                  </p>
                  <p className="font-body text-[13px] text-nav-slate m-0">{t('f5t6c.missed.rule')}</p>
                </div>
                {pages > 1 && (
                  <div className="px-6 pb-4">
                    <Pager
                      page={safePage}
                      pages={pages}
                      onPage={setPage}
                      prevLabel={t('f5t6c.pager.prev')}
                      nextLabel={t('f5t6c.pager.next')}
                      pageLabel={t('f5t6c.missed.pageLabel', {
                        n: visible.length,
                        total: all.length,
                        page: safePage + 1,
                        pages,
                      })}
                    />
                  </div>
                )}
              </div>
            )}

            {anyQueue && (
              <p id="missed-queue-why" className="font-body text-[12.5px] text-nav-slate mt-3 m-0">
                {t('f5t6c.missed.queueDisabled')}
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function MissedRow({
  aged,
  index,
  t,
  formatWhen,
  rescheduling,
  pickerOpen,
  newDate,
  setNewDate,
  onOpenPicker,
  onCancelPicker,
  onConfirm,
}: {
  aged: AgedRow;
  index: number;
  t: ReturnType<typeof useT>;
  formatWhen: (iso: string) => string;
  rescheduling: boolean;
  pickerOpen: boolean;
  newDate: string;
  setNewDate: (v: string) => void;
  onOpenPicker: () => void;
  onCancelPicker: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const { row, hours, days, mode } = aged;
  const aging = days <= 0 ? t('f5t6c.missed.aged.h', { n: Math.max(1, hours) }) : t('f5t6c.missed.aged.d', { n: days });
  const amber = days > 3;

  return (
    <li
      className={cn('border-t border-nav-hair first:border-t-0', rescheduling && 'opacity-70 pointer-events-none')}
      aria-busy={rescheduling || undefined}
    >
      <div
        style={{ animationDelay: `${index * 50}ms` }}
        className="flex items-center gap-4 flex-wrap min-h-[78px] px-6 py-4 animate-fade-up motion-reduce:animate-none"
      >
        {/* CÁPSULA DE ANTIGÜEDAD — el orden lo decide la demora, no la fecha.
            La antigüedad va en TEXTO; el ámbar es refuerzo (>3 días). */}
        <span
          className={cn(
            'inline-flex items-center justify-center min-w-[84px] min-h-[44px] px-3 rounded-lg border-[1.5px] font-head text-[12.5px] font-bold tabular-nums whitespace-nowrap flex-none',
            amber
              ? 'bg-nav-amberTint border-[#e6cf9a] text-nav-amberText'
              : 'bg-nav-hair border-nav-card text-nav-slate',
          )}
        >
          {aging}
        </span>

        <span className="flex-1 min-w-[220px]">
          <span className="block font-head text-[13.5px] font-bold text-nav-navy">
            {row.patientLastName}
          </span>
          <span className="block font-body text-[12.5px] text-nav-slate tabular-nums">
            {t('f5t6c.missed.meta', { mrn: row.patientMrnLast6, when: formatWhen(row.scheduledAt) })}
          </span>
        </span>

        <span className="flex-none min-w-[140px]">
          <span className="block font-body text-[12.5px] text-nav-slate">{t('f5t6c.missed.agendo')}</span>
          <span className="block font-head text-[13px] font-bold text-nav-navy">{row.agentName}</span>
        </span>

        {/* NECESITA-SERVER: columna «Intentos N de 5» — el endpoint no trae
            el conteo de intentos del paciente. */}

        <span className="flex items-center gap-2 flex-none">
          {rescheduling ? (
            <span className="inline-flex items-center min-h-[44px] px-[15px] rounded-lg border-[1.5px] border-nav-card bg-nav-hair font-head text-[12.5px] font-bold text-nav-slate">
              {t('f5t6c.missed.action.rescheduling')}
            </span>
          ) : mode === 'queue' ? (
            <button
              type="button"
              disabled
              aria-describedby="missed-queue-why"
              className="inline-flex items-center min-h-[44px] px-[15px] rounded-lg border-[1.5px] border-nav-card bg-[#f2f5f7] font-head text-[12.5px] font-bold text-nav-faint cursor-not-allowed"
            >
              {t('f5t6c.missed.action.queue')}
            </button>
          ) : (
            <button
              type="button"
              onClick={pickerOpen ? onCancelPicker : onOpenPicker}
              aria-expanded={pickerOpen}
              className="inline-flex items-center min-h-[44px] px-[15px] rounded-lg border-[1.5px] border-nav-navy bg-nav-navy font-head text-[12.5px] font-bold text-white hover:bg-nav-navyHover transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
            >
              {t('f5t6c.missed.action.resched')}
            </button>
          )}
          <Link
            to={`/clients/${row.patientId}`}
            className="inline-flex items-center min-h-[44px] px-3.5 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
          >
            {t('f5t6c.missed.action.view')}
          </Link>
        </span>
      </div>

      {/* La decisión que abre «Reagendar»: fecha nueva, dicha antes de tocar
          nada (muere window.confirm; diálogo propio inline). */}
      {pickerOpen && !rescheduling && (
        <div className="flex items-end gap-2.5 flex-wrap px-6 pb-4 bg-[#f4fafa] pt-3 border-t border-nav-hair">
          <label className="block">
            <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate mb-1">
              {t('f5t6c.missed.pick')}
            </span>
            <input
              type="datetime-local"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="box-border min-h-[44px] px-3 border-[1.5px] border-nav-line rounded-lg font-body text-[14px] text-nav-navy bg-white tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            />
          </label>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!newDate}
            className="inline-flex items-center min-h-[44px] px-[18px] rounded-lg border-[1.5px] border-nav-navy bg-nav-navy font-head text-[12.5px] font-bold text-white hover:bg-nav-navyHover disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
          >
            {t('f5t6c.missed.action.confirm')}
          </button>
          <button
            type="button"
            onClick={onCancelPicker}
            className="inline-flex items-center min-h-[44px] px-[14px] rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[12.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            {t('f5t6c.missed.action.cancel')}
          </button>
        </div>
      )}
    </li>
  );
}
